import { RefObject, useEffect, useState } from 'react';

import { LngLat, LngLatBounds, LngLatBoundsLike } from 'mapbox-gl';

import mapbox from '@domain/map/mapboxClient';

import { DEFAULT_HEADERS } from '../../api/rest/shared';
import {
  DEFAULT_MAP_STYLE,
  MAP_MAXBOUNDS_EAST,
  MAP_MAXBOUNDS_NORTH,
  MAP_MAXBOUNDS_SOUTH,
  MAP_MAXBOUNDS_WEST,
  MAP_STYLE_URLS,
  MapStyle,
  RUTILUS_PRODUCTION_DOMAIN,
  RUTILUS_STAGING_DOMAIN,
} from '../../components/Maps/constants/constants';
import {
  addMapboxLayers,
  filterCatchPositions,
  filterLayersBasedOnToggleableGroups,
  filterWater,
  orderMapboxLayers,
  removeMapboxLayers,
} from '../../components/Maps/mapLoading/editLayers';
import { appendSources, removeSources } from '../../components/Maps/mapLoading/editSources';
import { CatchFilters, FishbrainLayer } from '../../components/Maps/types/types';
import { WEB_GL_DISABLED_ERROR_MESSAGE } from '@config/constants';
import { useMap } from '@domain/map/MapContext';
import { useMapLayers } from '@domain/map/MapLayersContext';
import { ICoordinates } from '@app-types/geo';
import { _getAccessToken } from '@domain/auth/tokenStorage';
import { reportMykissClientError } from '@lib/errors/client';
import { useWindowSize } from '@hooks/useWindowSize';
import { logger } from '@lib/logger/logger';
import { shallowRemoveBlankValues } from '@lib/objects/shallowRemoveBlankValues';
import { getSecondsSinceEpoch } from '@lib/date/getSecondsSinceEpoch';

import { SMALL } from '@hooks/useScreenWidth';
import { useUserInfo } from '@domain/user/useUserInfo';

export enum MapboxExpectedError {
  WebGLDisabled = 0,
  Other = 1,
}

const MapOptionsDefaults: IMapOptions = {
  waterId: undefined,
  showGeolocateControl: true,
  navControlPosition: 'bottom-right',
  disableDragPan: false,
  fibLayers: ['fishingWaters'],
  catchFilters: { months: [], speciesIds: [] },
  mapStyle: DEFAULT_MAP_STYLE,
  minZoom: 0,
  maxZoom: 22,
  cooperativeGestures: false,
  logoPosition: 'bottom-left',
  attributionButtonPosition: 'bottom-right',
  showFullscreenControl: false,
};

interface IMapOptions {
  waterId: string | undefined;
  showGeolocateControl: boolean;
  navControlPosition: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  disableDragPan: boolean;
  fibLayers: FishbrainLayer[];
  catchFilters: CatchFilters;
  mapStyle: MapStyle;
  minZoom?: number;
  maxZoom?: number;
  cooperativeGestures: boolean;
  logoPosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  attributionButtonPosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  showFullscreenControl: boolean;
  maxBounds?: LngLatBoundsLike;
}

interface IProps {
  error?: MapboxExpectedError;
}

export function useNewMap(
  container: RefObject<HTMLDivElement | null>,
  zoom: number,
  coordinates: ICoordinates,
  options?: Partial<IMapOptions>,
): IProps {
  const [error, setError] = useState<MapboxExpectedError>();
  const size = useWindowSize();
  const { isPremium, isLoadingUser } = useUserInfo();
  const { map, setMap, setMapLayersLoaded, isMapPreloaded, setIsMapPreloaded } = useMap();
  const { layers: toggleableLayers } = useMapLayers();

  const {
    fibLayers,
    waterId,
    navControlPosition,
    showGeolocateControl,
    disableDragPan,
    catchFilters,
    mapStyle,
    minZoom,
    maxZoom,
    logoPosition,
    attributionButtonPosition,
    cooperativeGestures,
    showFullscreenControl,
    maxBounds: customMaxBounds,
  } = {
    ...MapOptionsDefaults,
    ...shallowRemoveBlankValues(options ?? {}),
  };

  const layersAsString = fibLayers.toString();

  // handles loading and changing Fishbrain layers
  useEffect(() => {
    if (isMapPreloaded && map && !isLoadingUser) {
      setMapLayersLoaded(false);

      removeMapboxLayers(map, fibLayers);
      removeSources(map, fibLayers);
      appendSources(map, isPremium, fibLayers);
      addMapboxLayers(map, fibLayers);
      orderMapboxLayers(map, fibLayers);
      filterCatchPositions(map, catchFilters);

      filterLayersBasedOnToggleableGroups({
        map,
        layers: toggleableLayers,
      });

      if (waterId) {
        filterWater(map, waterId);
      }

      setMapLayersLoaded(true);
    }

    return () => {
      setMapLayersLoaded(false);
    };
    // We convert layers to a string so we don't get stuck in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layersAsString,
    isMapPreloaded,
    isPremium,
    isLoadingUser,
    map,
    setMapLayersLoaded,
    waterId,
    toggleableLayers,
    catchFilters,
  ]);

  useEffect(() => {
    const maxBounds =
      customMaxBounds ??
      new LngLatBounds(
        new LngLat(MAP_MAXBOUNDS_SOUTH, MAP_MAXBOUNDS_WEST),
        new LngLat(MAP_MAXBOUNDS_NORTH, MAP_MAXBOUNDS_EAST),
      );

    if (container.current) {
      try {
        logger.debug('Instantiating map component');
        const { latitude, longitude } = coordinates;
        logger.debug('Creating new map instance (i.e. we pay for this)');
        const newMap = new mapbox.Map({
          zoom,
          center: [longitude, latitude],
          container: container.current,
          style: MAP_STYLE_URLS[mapStyle],
          attributionControl: false,
          transformRequest: url => {
            if (
              url.startsWith(`${RUTILUS_STAGING_DOMAIN}/tiles/waypoints`) ||
              url.startsWith(`${RUTILUS_PRODUCTION_DOMAIN}/tiles/waypoints`) ||
              url.startsWith(`${RUTILUS_STAGING_DOMAIN}/v2/tiles/catches`) ||
              url.startsWith(`${RUTILUS_PRODUCTION_DOMAIN}/v2/tiles/catches`)
            ) {
              return {
                url,
                // TODO: Expand this to cover use cases where the token expires during a user session (_get returns '')
                headers: {
                  Authorization: `Bearer ${_getAccessToken()}`,
                  timestamp: getSecondsSinceEpoch().toString(),
                  ...DEFAULT_HEADERS,
                },
              };
            }
            return { url };
          },
          maxBounds,
          minZoom,
          maxZoom,
          cooperativeGestures,
          logoPosition,
        });

        newMap.addControl(
          new mapbox.AttributionControl({
            compact: true,
          }),
          attributionButtonPosition,
        );

        newMap.addControl(new mapbox.NavigationControl({ showCompass: false }), navControlPosition);

        if (showGeolocateControl) {
          if (size.width && size.width > SMALL) {
            newMap.addControl(
              new mapbox.GeolocateControl({
                positionOptions: {
                  enableHighAccuracy: true,
                },
                trackUserLocation: true,
              }),
              'bottom-right',
            );
          } else {
            newMap.addControl(
              new mapbox.GeolocateControl({
                positionOptions: {
                  enableHighAccuracy: true,
                },
                trackUserLocation: true,
              }),
              'bottom-left',
            );
          }
        }

        if (showFullscreenControl) {
          newMap.addControl(new mapbox.FullscreenControl(), 'top-right');
        }

        newMap.once('load', () => {
          setIsMapPreloaded(true);
        });

        if (disableDragPan) {
          newMap.dragPan.disable();
        } else {
          newMap.dragPan.enable();
        }

        // disable map rotation using right click + drag
        newMap.dragRotate.disable();

        // disable map rotation using touch rotation gesture
        newMap.touchZoomRotate.disableRotation();
        setMap(newMap);
        window.exploreMap = newMap;

        return () => {
          window.exploreMap = undefined;
          newMap.remove();
        };
      } catch (e) {
        if (e.message.includes(WEB_GL_DISABLED_ERROR_MESSAGE)) {
          // eslint-disable-next-line react-hooks/set-state-in-effect -- FIB-49117
          setError(MapboxExpectedError.WebGLDisabled);
        } else {
          reportMykissClientError(e);
        }
        return () => {
          window.exploreMap = undefined;
        };
      }
    }
    return () => {
      setIsMapPreloaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container]);
  return { error };
}
